import { useAbility } from "@casl/react";
import {
  AppliedFilters,
  Container,
  Table,
  useModal,
} from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";

import { ActionButtons } from "./components/action-buttons";
import { MailmapModals } from "./components/mailmap-modals";
import {
  useCreateMailmapEntryMutation,
  useDeleteMailmapEntryWithSubentriesMutation,
  useImportMailmapMutation,
  useMoveMailmapEntryWithSubentriesMutation,
  useSetMailmapEntryAsMailmapSubentryMutation,
  useUpdateMailmapEntryMutation,
} from "./hooks/use-mailmap-mutations";
import { useMailmapRecords } from "./hooks/use-mailmap-records";
import type { IMailmapProps } from "./types";
import { getColumns, removeDuplicateEntryRecords } from "./utils";

import { Authorize } from "components/@core/authorize";
import { SectionHeader } from "components/section-header";
import { authzPermissionsContext } from "context/authz/config";
import { useMailmapFilters } from "features/mailmap/filters";
import { renderMailmapSubentries } from "features/mailmap/subentries";
import type {
  ICreateMailmapEntryFormValues,
  IDeleteMailmapEntryFormValues,
  IImportMailmapFormValues,
  IMailmapEntry,
  IMailmapRecord,
  IMoveMailmapEntryWithSubentriesFormValues,
  ISetMailmapEntryAsMailmapSubentryFormValues,
  IUpdateMailmapEntryFormValues,
} from "features/mailmap/types";
import { useTable } from "hooks/use-table";

const Mailmap: React.FC<IMailmapProps> = ({
  organizationId,
}: IMailmapProps): JSX.Element => {
  const theme = useTheme();
  const { t } = useTranslation();

  const permissions = useAbility(authzPermissionsContext);
  // This particular mutation below was chosen arbitrarily
  const mutation = "integrates_api_mutations_create_mailmap_entry_mutate";
  const canMutate = permissions.can(mutation);

  const [filteredDataset, setFilteredDataset] = useState<IMailmapRecord[]>([]);

  const createEntryModal = useModal("create-entry-modal");
  const onCloseCreateEntryModal = createEntryModal.close;
  const onOpenCreateEntryModal = createEntryModal.open;

  const deleteEntryModal = useModal("delete-entry-modal");
  const onCloseDeleteEntryModal = deleteEntryModal.close;
  const onOpenDeleteEntryModal = deleteEntryModal.open;

  const updateEntryModal = useModal("update-entry-modal");
  const onCloseUpdateEntryModal = updateEntryModal.close;
  const onOpenUpdateEntryModal = updateEntryModal.open;

  const setEntryAsSubentryModal = useModal("set-entry-as-subentry-modal");
  const onCloseSetEntryAsSubentryModal = setEntryAsSubentryModal.close;
  const onOpenSetEntryAsSubentryModal = setEntryAsSubentryModal.open;

  const moveEntryWithSubentriesModal = useModal(
    "move-entry-with-subentries-modal",
  );
  const onCloseMoveEntryWithSubentriesModal =
    moveEntryWithSubentriesModal.close;
  const onOpenMoveEntryWithSubentriesModal = moveEntryWithSubentriesModal.open;

  const importMailmapModal = useModal("import-mailmap-modal");
  const onCloseImportMailmapModal = importMailmapModal.close;
  const onOpenImportMailmapModal = importMailmapModal.open;

  const [search, setSearch] = useState("");

  const [isEntryConfigMenuVisible, setIsEntryConfigMenuVisible] =
    useState<boolean>(false);

  /*
   * The purpose of using mailmap records instead of mailmap entries with
   * subentries is because it's easier to apply filters to mailmap records.
   * We get records with duplicate entry name and entry email, and that0s why
   * we need `removeDuplicateEntryRecords` function later.
   */
  const { records, refetch } = useMailmapRecords({ organizationId });

  const [createMailmapEntryMutation] = useCreateMailmapEntryMutation();

  const [deleteMailmapEntryWithSubentriesMutation] =
    useDeleteMailmapEntryWithSubentriesMutation();

  const [updateMailmapEntryMutation] = useUpdateMailmapEntryMutation();

  const [setMailmapEntryAsMailmapSubentryMutation] =
    useSetMailmapEntryAsMailmapSubentryMutation();

  const [moveMailmapEntryWithSubentriesMutation] =
    useMoveMailmapEntryWithSubentriesMutation();

  const [importMailmapMutation, { loading: importMailmapSubmitting }] =
    useImportMailmapMutation();

  const entriesEmails = useMemo(
    (): string[] =>
      records.map((record: IMailmapRecord): string => record.mailmapEntryEmail),
    [records],
  );

  const rowExpansion = useCallback(
    (row: Row<IMailmapEntry>): JSX.Element => {
      const entryEmail = row.original.mailmapEntryEmail;

      return renderMailmapSubentries({
        entriesEmails,
        entryEmail,
        organizationId,
        refetchEntries: refetch,
      });
    },
    [refetch, organizationId, entriesEmails],
  );

  // Table config
  const formattedData = useMemo(
    (): IMailmapRecord[] =>
      removeDuplicateEntryRecords(filteredDataset).filter(
        ({ mailmapEntryEmail: email, mailmapEntryName: name }): boolean =>
          email.toLowerCase().includes(search.toLowerCase()) ||
          name.toLowerCase().includes(search.toLowerCase()),
      ),
    [filteredDataset, search],
  );

  const { table } = useTable({
    columns: getColumns(records),
    data: formattedData,
    id: "mailmap-table",
    options: {
      actionsOptions: { onSearch: setSearch, search },
      filtersMode: "client-side",
      paginationMode: "client-side",
      rowOptions: {
        enableMultiSelection: false,
        enableRowSelection: canMutate,
        expandRender: rowExpansion,
      },
    },
    total: formattedData.length,
  });

  const { rowSelection } = table.getState();
  const { data: tableData } = table.options.meta ?? {};
  const checkedAuthors: IMailmapEntry[] = Object.keys(rowSelection).map(
    (key): IMailmapEntry => tableData?.get(key) ?? ({} as IMailmapEntry),
  );

  const createMailmapEntry = useCallback(
    async (values: ICreateMailmapEntryFormValues): Promise<void> => {
      const { entryEmail, entryName } = values;
      await createMailmapEntryMutation({
        variables: {
          entry: {
            mailmapEntryEmail: entryEmail,
            mailmapEntryName: entryName,
          },
          organizationId,
        },
      });
      onCloseCreateEntryModal();
    },
    [createMailmapEntryMutation, onCloseCreateEntryModal, organizationId],
  );

  const deleteMailmapEntry = useCallback(
    async (values: IDeleteMailmapEntryFormValues): Promise<void> => {
      const { entryEmail } = values;
      await deleteMailmapEntryWithSubentriesMutation({
        variables: {
          entryEmail,
          organizationId,
        },
      });
      onCloseDeleteEntryModal();
      table.setRowSelection({});
    },
    [
      deleteMailmapEntryWithSubentriesMutation,
      onCloseDeleteEntryModal,
      organizationId,
      table,
    ],
  );

  const updateMailmapEntry = useCallback(
    async (values: IUpdateMailmapEntryFormValues): Promise<void> => {
      const { oldEntryEmail, newEntryEmail, newEntryName } = values;
      await updateMailmapEntryMutation({
        variables: {
          entry: {
            mailmapEntryEmail: newEntryEmail,
            mailmapEntryName: newEntryName,
          },
          entryEmail: oldEntryEmail,
          organizationId,
        },
      });
      onCloseUpdateEntryModal();
      table.setRowSelection({});
    },
    [
      updateMailmapEntryMutation,
      organizationId,
      onCloseUpdateEntryModal,
      table,
    ],
  );

  const setMailmapEntryAsMailmapSubentry = useCallback(
    async (
      values: ISetMailmapEntryAsMailmapSubentryFormValues,
    ): Promise<void> => {
      const { entryEmail, targetEntryEmail } = values;
      await setMailmapEntryAsMailmapSubentryMutation({
        variables: {
          entryEmail,
          organizationId,
          targetEntryEmail,
        },
      });
      onCloseSetEntryAsSubentryModal();
      table.setRowSelection({});
    },
    [
      setMailmapEntryAsMailmapSubentryMutation,
      organizationId,
      onCloseSetEntryAsSubentryModal,
      table,
    ],
  );

  const moveMailmapEntryWithSubentries = useCallback(
    async (
      values: IMoveMailmapEntryWithSubentriesFormValues,
    ): Promise<void> => {
      const { entryEmail, newOrganizationId } = values;
      await moveMailmapEntryWithSubentriesMutation({
        variables: {
          entryEmail,
          newOrganizationId,
          organizationId,
        },
      });
      onCloseMoveEntryWithSubentriesModal();
      table.setRowSelection({});
    },
    [
      moveMailmapEntryWithSubentriesMutation,
      onCloseMoveEntryWithSubentriesModal,
      organizationId,
      table,
    ],
  );

  const importMailmap = useCallback(
    async (values: IImportMailmapFormValues): Promise<void> => {
      await importMailmapMutation({
        variables: {
          file: values.file[0],
          organizationId,
        },
      });
      onCloseImportMailmapModal();
    },
    [importMailmapMutation, onCloseImportMailmapModal, organizationId],
  );

  // Components Declaration

  const modals = (
    <MailmapModals
      checkedAuthors={checkedAuthors}
      createEntryModal={createEntryModal}
      createMailmapEntry={createMailmapEntry}
      deleteEntryModal={deleteEntryModal}
      deleteMailmapEntry={deleteMailmapEntry}
      importMailmap={importMailmap}
      importMailmapModal={importMailmapModal}
      importMailmapSubmitting={importMailmapSubmitting}
      moveEntryWithSubentriesModal={moveEntryWithSubentriesModal}
      moveMailmapEntryWithSubentries={moveMailmapEntryWithSubentries}
      setEntryAsSubentryModal={setEntryAsSubentryModal}
      setMailmapEntryAsMailmapSubentry={setMailmapEntryAsMailmapSubentry}
      updateEntryModal={updateEntryModal}
      updateMailmapEntry={updateMailmapEntry}
    />
  );

  const header = (
    <SectionHeader header={t("organization.tabs.mailmap.header")} />
  );

  const toggleEntryConfigMenu = useCallback((): void => {
    setIsEntryConfigMenuVisible((previous): boolean => !previous);
  }, [setIsEntryConfigMenuVisible]);

  const closeEntryConfigMenu = useCallback((): void => {
    setIsEntryConfigMenuVisible(false);
  }, []);

  const buttons = (
    <ActionButtons
      checkedAuthorsCount={checkedAuthors.length}
      isEntryConfigMenuVisible={isEntryConfigMenuVisible}
      onCloseEntryConfigMenu={closeEntryConfigMenu}
      onOpenCreateEntryModal={onOpenCreateEntryModal}
      onOpenDeleteEntryModal={onOpenDeleteEntryModal}
      onOpenImportMailmapModal={onOpenImportMailmapModal}
      onOpenMoveEntryWithSubentriesModal={onOpenMoveEntryWithSubentriesModal}
      onOpenSetEntryAsSubentryModal={onOpenSetEntryAsSubentryModal}
      onOpenUpdateEntryModal={onOpenUpdateEntryModal}
      onToggleEntryConfigMenu={toggleEntryConfigMenu}
    />
  );

  const { Filters, options, removeFilter } = useMailmapFilters({
    dataset: records,
    setFilteredDataset,
  });

  const tableRender = (
    <Container
      bgColor={theme.palette.gray[50]}
      height={"100hv"}
      pb={1.25}
      pl={1.25}
      pr={1.25}
      pt={1.25}
    >
      <Table table={table}>
        <Table.Options>
          <Table.Controls>
            <Filters />
            <Authorize can={mutation}>
              <Container gap={0.5} id={"actions-container"}>
                {buttons}
              </Container>
            </Authorize>
          </Table.Controls>
        </Table.Options>
        <Table.Caption>
          <AppliedFilters onClose={removeFilter} options={options} />
        </Table.Caption>
        <Table.Content />
        <Table.Footer />
      </Table>
    </Container>
  );

  return (
    <React.Fragment>
      {modals}
      {header}
      {tableRender}
    </React.Fragment>
  );
};

export { Mailmap };
